import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import { accountClassesWithTypes } from "@common/constants/domains/accounting/helpers";
import {
    getImportedAccountMappingResolution,
    ImportedAccountMappingResolution,
} from "@common/helpers/importedAccountMappingResolution";
import { Account } from "@common/types/domains/accounting/account";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import editorStyles from "@components/settings/ChartOfAccountsTemplates/editor/ChartOfAccountsTemplateEditor.module.scss";
import { CoaMappingRow } from "../coaMappingRowTypes";
import { useCoaMappingCellData } from "./useCoaMappingCellData";
import styles from "./CoaMappingCells.module.scss";

export const CoaMappingAccountCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, CoaMappingRow, unknown>) => {
        const { account, kickAccount, isMappingLoading, options, callbacks } =
            useCoaMappingCellData({ row: row.original, table });
        const importedAccountId = account?.id;
        const mappedAccountId = kickAccount?.id ?? null;
        const onMapAccount = callbacks?.onMapAccount;
        const onOpenCreateAccountModal = callbacks?.onOpenCreateAccountModal;

        const handleChange = useCallback(
            (selectedAccount: Account | null) => {
                const accountId = selectedAccount?.id ?? null;
                if (!importedAccountId || accountId === mappedAccountId) {
                    return;
                }
                onMapAccount?.({ importedAccountId, accountId });
            },
            [importedAccountId, mappedAccountId, onMapAccount],
        );

        const handleCreateFromInput = useCallback(
            (name: string) => {
                if (importedAccountId) {
                    onOpenCreateAccountModal?.({ importedAccountId, name });
                }
            },
            [importedAccountId, onOpenCreateAccountModal],
        );

        const handleCreateFromEmptyInput = useCallback(() => {
            if (importedAccountId) {
                onOpenCreateAccountModal?.({ importedAccountId });
            }
        }, [importedAccountId, onOpenCreateAccountModal]);

        if (!account || !options) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell
                captureClicks
                className={
                    isMappingLoading
                        ? `animation-shimmer ${styles.loadingCell}`
                        : undefined
                }
            >
                <span
                    className={editorStyles.editorCellAnchor}
                    data-editor-cell={`${account.id}:account`}
                >
                    <AccountCombobox
                        entityId={options.entityId}
                        selectedAccount={kickAccount}
                        onChange={handleChange}
                        showAccountCode={options.showAccountNumbers}
                        allowedAccountTypes={
                            accountClassesWithTypes[account.classification]
                        }
                        includeAccountsLinkedToFinancialAccount
                        addNewAccountButton={options.isCreateAccountAvailable}
                        onCreateFromInput={
                            options.isCreateAccountAvailable
                                ? handleCreateFromInput
                                : undefined
                        }
                        onCreateFromEmptyInput={
                            options.isCreateAccountAvailable
                                ? handleCreateFromEmptyInput
                                : undefined
                        }
                        canClear
                        trigger={
                            getImportedAccountMappingResolution({ account }) ===
                            ImportedAccountMappingResolution.NEW_ACCOUNT ? (
                                <Picker.CellContent value={account.name} />
                            ) : undefined
                        }
                    />
                </span>
            </ViewTableCell>
        );
    },
);

CoaMappingAccountCell.displayName = "CoaMappingAccountCell";
